import React, {
	type ReactNode,
	Suspense,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { getATLContextUrl } from '@atlaskit/atlassian-context/get-atl-context-url';
import { GiveKudosLauncherLazy, KudosType } from '@atlaskit/give-kudos';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import { CardCloseGuardContext } from '../../store/cardCloseGuard';
import type { ActionMenuItem } from './ActionMenu';

const messages = defineMessages({
	giveKudosLabel: {
		id: 'user-profile-card.useKudosMenuItem.giveKudosLabel',
		defaultMessage: 'Give kudos',
		description: 'Label for the action menu entry that opens the Give Kudos drawer',
	},
});

/**
 * Stable analytics source attributed to kudos drawer events fired from this card.
 */
const ANALYTICS_SOURCE = 'user-profile-card';

interface UseKudosMenuItemArgs {
	accountId: string;
	cloudId: string;
	viewerId?: string;
	/** "More actions" button; focus returns here when the drawer closes. */
	actionMenuTriggerRef?: React.RefObject<HTMLElement | null>;
}

interface UseKudosMenuItemResult {
	menuItem: ActionMenuItem | null;
	launcher: ReactNode | null;
}

/**
 * Encapsulates the Give-Kudos integration for the action menu.
 */
export function useKudosMenuItem({
	accountId,
	cloudId,
	viewerId,
	actionMenuTriggerRef,
}: UseKudosMenuItemArgs): UseKudosMenuItemResult {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);

	// Getter so an unmounted trigger resolves to `null`; focusing a detached node drops focus
	// to `<body>`.
	const focusReturnRef = useMemo<React.RefObject<HTMLElement | null>>(
		() => ({
			get current() {
				const element = actionMenuTriggerRef?.current ?? null;
				return element && element.isConnected ? element : null;
			},
		}),
		[actionMenuTriggerRef],
	);

	// Keep the card open while the drawer is up: clicks on the portalled drawer count as outside
	// clicks. Released on `onCloseComplete`, since trailing events can follow `onClose`.
	const cardCloseGuardRef = useContext(CardCloseGuardContext);
	const holdCardOpen = useCallback(
		(held: boolean) => {
			if (cardCloseGuardRef) {
				cardCloseGuardRef.current = held;
			}
		},
		[cardCloseGuardRef],
	);
	// Never leave the card stuck open if this unmounts mid-drawer.
	useEffect(() => () => holdCardOpen(false), [holdCardOpen]);

	const onClose = useCallback(() => setIsOpen(false), []);
	const onCloseComplete = useCallback(() => holdCardOpen(false), [holdCardOpen]);
	const onOpen = useCallback(() => {
		if (fg('teams_a11y_focus_high_priority')) {
			holdCardOpen(true);
		}
		setIsOpen(true);
	}, [holdCardOpen]);

	const recipient = useMemo(
		() => ({ type: KudosType.INDIVIDUAL as const, recipientId: accountId }),
		[accountId],
	);

	const label = formatMessage(messages.giveKudosLabel);
	const menuItem = useMemo<ActionMenuItem>(
		() => ({ kind: 'button', id: 'give-kudos', label, onClick: onOpen }),
		[label, onOpen],
	);

	const teamCentralBaseUrl = getATLContextUrl('home');
	const suppressed = !teamCentralBaseUrl || (!!viewerId && viewerId === accountId);

	if (suppressed) {
		return { menuItem: null, launcher: null };
	}

	const isFocusA11yEnabled = fg('teams_a11y_focus_high_priority');
	const launcher = (
		<Suspense fallback={null}>
			<GiveKudosLauncherLazy
				isOpen={isOpen}
				onClose={onClose}
				recipient={recipient}
				analyticsSource={ANALYTICS_SOURCE}
				teamCentralBaseUrl={teamCentralBaseUrl}
				cloudId={cloudId}
				triggerRef={isFocusA11yEnabled ? focusReturnRef : undefined}
				onCloseComplete={isFocusA11yEnabled ? onCloseComplete : undefined}
			/>
		</Suspense>
	);

	return { menuItem, launcher };
}
